/**
 * @file currencyFormatter.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-11
 * @modified 2026-11-02
 * @description Centralized currency formatting utility
 *
 * This module consolidates all currency formatting functions previously
 * duplicated across 8+ pricing component files.
 *
 * 1.2.0 (2026-11-02): shared with the server — utils/currencyFormatter.js is
 * a CommonJS copy written by `node scripts/sync-shared-utils.js` (the Invoice
 * Detail report's export is built on the server and must print each figure
 * exactly as the screen shows it). Edit here and re-run that script; keep the
 * file free of imports and `export default` (the default object nobody
 * imported was taken out so the copy can be generated).
 */

/**
 * Format a value as GBP currency
 * @param {string|number} value - Value to format
 * @param {Object} options - Formatting options
 * @param {number} options.minimumFractionDigits - Minimum decimal places (default: 2)
 * @param {number} options.maximumFractionDigits - Maximum decimal places (default: 2)
 * @param {boolean} options.showSymbol - Whether to show £ symbol (default: true)
 * @returns {string} Formatted currency or '-' if invalid
 */
export const formatCurrency = (value, options = {}) => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showSymbol = true
  } = options;

  if (value === null || value === undefined || value === '') return '-';

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) return '-';

  const formatted = new Intl.NumberFormat('en-GB', {
    style: showSymbol ? 'currency' : 'decimal',
    currency: 'GBP',
    minimumFractionDigits,
    maximumFractionDigits
  }).format(num);

  return formatted;
};

/**
 * The most decimal places a unit price, cost or quantity carries anywhere in
 * the system. Every such column is numeric(12,4), and Xero's own ceiling is 4
 * (the `unitdp` it is called with) — there is no fifth place to show.
 */
export const MAX_UNIT_DP = 4;

/**
 * A unit price or cost, as the system shows it everywhere: never fewer than 2
 * decimal places, and as many more — up to 4 — as the stored figure actually
 * has. £236 reads "£236.00"; £236.125 reads "£236.125"; £236.1250 (a 4dp column
 * padding a 3dp figure) still reads "£236.125". Rounding a real £236.125 to
 * "£236.13" made a line look as though its total did not add up.
 *
 * For PRICES AND COSTS ONLY. Line totals, tax and document totals are money
 * that has been settled to the penny and stay at formatCurrency's 2dp.
 */
export const formatPrice = (value, { showSymbol = true } = {}) => formatCurrency(value, {
  minimumFractionDigits: 2,
  maximumFractionDigits: MAX_UNIT_DP,
  showSymbol,
});

/**
 * Format a value as GBP currency without symbol
 * @param {string|number} value - Value to format
 * @returns {string} Formatted number or '-' if invalid
 */
export const formatCurrencyWithoutSymbol = (value) => {
  return formatCurrency(value, { showSymbol: false });
};

/**
 * Format a value as GBP currency with no decimal places
 * @param {string|number} value - Value to format
 * @returns {string} Formatted currency or '-' if invalid
 */
export const formatCurrencyWhole = (value) => {
  return formatCurrency(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

/**
 * Format a value as a percentage
 * @param {string|number} value - Value to format (e.g., 0.15 for 15%)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage or '-' if invalid
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || value === '') return '-';

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) return '-';

  return new Intl.NumberFormat('en-GB', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
};

/**
 * Format a number with thousand separators
 * @param {string|number} value - Value to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted number or '-' if invalid
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return '-';

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) return '-';

  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
};

/**
 * Parse a formatted currency string back to a number
 * @param {string} currencyString - Formatted currency string (e.g., "£1,234.56")
 * @returns {number|null} Parsed number or null if invalid
 */
export const parseCurrency = (currencyString) => {
  if (!currencyString || typeof currencyString !== 'string') return null;

  // Remove currency symbol, spaces, and commas
  const cleaned = currencyString.replace(/[£$€,\s]/g, '');
  const num = parseFloat(cleaned);

  return isNaN(num) ? null : num;
};

/**
 * Format a value as compact currency (e.g., £1.2K, £3.4M)
 * @param {string|number} value - Value to format
 * @returns {string} Compact formatted currency or '-' if invalid
 */
export const formatCurrencyCompact = (value) => {
  if (value === null || value === undefined || value === '') return '-';

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) return '-';

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(num);
};
